import { apiClient } from './client';

// ── Types ────────────────────────────────────────────────────────────

export type SignificanceLevel = 'routine' | 'notable' | 'significant' | 'critical';

export interface OsintSignal {
  id: string;
  source_id: string;
  source_group: string;
  signal_type: string;
  geo_region: string | null;
  entity_ref: string | null;
  content_hash: string;
  normalised_data: Record<string, unknown>;
  investigation_id: string | null;
  collected_at: string;
  significance: SignificanceLevel | null;
  significance_reason: string | null;
}

export interface PaginatedSignalsResponse {
  signals: OsintSignal[];
  /**
   * Exact match count, or null when the server stopped counting at its cap
   * (#750). Never treat `total_lower_bound` as an exact total.
   */
  total: number | null;
  total_is_exact: boolean;
  /** Equals `total` when exact; otherwise "more than the cap" lower bound. */
  total_lower_bound: number;
  limit: number;
  offset: number;
}

export interface SourceHealthDetail {
  source_id: string;
  status: 'active' | 'cooldown' | 'error' | 'idle';
  last_polled_at: string | null;
  last_signal_at: string | null;
  last_error: string | null;
  poll_count: number;
  signal_count: number;
}

export interface OsintHealthResponse {
  feeds_online: number;
  feeds_total: number;
  feeds_polling: number;
  signal_latency_sec: number | null;
  last_poll_at: string | null;
  escalation_queue_depth: number;
  replay_workers_active: number;
  sources: SourceHealthDetail[];
}

export interface SignalSummaryResponse {
  total_signals: number;
  by_source_group: Record<string, number>;
  by_significance: Record<string, number>;
  counter_signals: number;
  certificate_candidates: number;
  convergence_cells: number;
}

export type SuggestionType = 'paradox_investigation' | 'forge_opportunity' | 'temporal_cluster' | 'intelligence_brief';

export interface InvestigationSuggestion {
  suggestion_id: string;
  suggestion_type: SuggestionType;
  title: string;
  reason: string;
  significance: SignificanceLevel;
  source_group: string;
  triggering_signal_ids: string[];
  triggering_source_ids: string[];
  signal_count: number;
  metadata: Record<string, unknown>;
}

export interface SuggestionsResponse {
  suggestions: InvestigationSuggestion[];
  total: number;
}

export interface SuggestionPromoteRequest {
  suggestion_type: SuggestionType;
  title: string;
  source_group: string;
  triggering_signal_ids: string[];
  triggering_source_ids?: string[];
  metadata: Record<string, unknown>;
}

// Cycle 086b — Focal Point Detector
export interface FocalPoint {
  h3_cell: string | null;
  geo_label: string | null;
  focal_score: number;
  urgency: 'critical' | 'elevated' | 'watch' | 'background';
  signal_count: number;
  domain_count: number;
  domains: string[];
  score_breakdown: {
    signal: number;
    significance: number;
    theatre: number;
    paradox: number;
    aeon: number;
  };
  suggested_action: string;
  contributing_signal_ids: string[];
}

export interface FocalPointsResponse {
  focal_points: FocalPoint[];
  total: number;
}

// Cycle 088 — Intelligence Brief
export interface DomainStatus {
  domain: string;
  severity: 'ALERT' | 'CAUTION' | 'WATCH' | 'ROUTINE';
  indicator_count: number;
  top_region: string;
  regional_breakdown: Record<string, number>;
}

export interface FocalPointSummary {
  cell_or_label: string;
  urgency: 'critical' | 'elevated' | 'watch' | 'background';
  score: number;
  contributing_source_groups: string[];
}

export interface ConvergenceEvent {
  h3_cell: string;
  source_group_count: number;
  total_signals: number;
  score: number;
}

export interface BaselineAnomaly {
  source_group: string;
  signal_type: string;
  z_score: number;
  threshold: 'notable' | 'critical';
}

export interface IntelligenceBriefResponse {
  timestamp: string;
  global_threat_level: 'CRITICAL' | 'ALERT' | 'CAUTION' | 'WATCH' | 'ROUTINE';
  domains: DomainStatus[];
  focal_points_summary: FocalPointSummary[];
  convergence_events: ConvergenceEvent[];
  anomalies: BaselineAnomaly[];
  velocity_flags: string[];
  total_signals_24h: number;
  source_group_distribution: Record<string, number>;
}

export interface SignalQueryParams {
  /** Legacy single-value filter. Mutually exclusive with `source_groups`. */
  source_group?: string;
  /**
   * N:M source-group filter per signal-catalog.md §3.A.2 (cycle-101.1 sprint-2).
   * Serialized to wire as comma-separated `source_groups=a,b,c` for FastAPI.
   * When both `source_group` and a non-empty `source_groups` are provided,
   * `source_groups` wins (legacy field is ignored).
   */
  source_groups?: readonly string[];
  source_id?: string;
  investigation_id?: string;
  /** Comma-separated signal IDs for batch fetch. */
  ids?: string;
  since?: string;
  limit?: number;
  offset?: number;
}

// ── API ──────────────────────────────────────────────────────────────

export const osintApi = {
  async getSignals(
    params: SignalQueryParams = {},
    signal?: AbortSignal,
  ): Promise<PaginatedSignalsResponse> {
    // §3.A.2: serialize source_groups array as comma-separated string; strip
    // the legacy source_group when source_groups is non-empty (array wins).
    const { source_groups, source_group, ...rest } = params;
    const wire: Record<string, unknown> = { ...rest };
    if (source_groups && source_groups.length > 0) {
      wire.source_groups = source_groups.join(',');
    } else if (source_group) {
      wire.source_group = source_group;
    }
    const { data } = await apiClient.get('/api/v1/osint/signals', { params: wire, signal });
    return data;
  },

  async getHealth(): Promise<OsintHealthResponse> {
    const { data } = await apiClient.get('/api/v1/osint/health');
    return data;
  },

  async getSummary(): Promise<SignalSummaryResponse> {
    const { data } = await apiClient.get('/api/v1/osint/signals/summary');
    return data;
  },

  async getSuggestions(limit = 15): Promise<SuggestionsResponse> {
    const { data } = await apiClient.get('/api/v1/osint/signals/suggestions', {
      params: { limit },
    });
    return data;
  },

  async promoteSuggestion(body: SuggestionPromoteRequest): Promise<{ id: string }> {
    const { data } = await apiClient.post('/api/v1/osint/signals/suggestions/promote', body);
    return data;
  },

  async getFocalPoints(
    lookbackHours = 24,
    limit = 20,
    signal?: AbortSignal,
  ): Promise<FocalPointsResponse> {
    const { data } = await apiClient.get('/api/v1/osint/signals/focal-points', {
      params: { lookback_hours: lookbackHours, limit },
      signal,
    });
    return data;
  },

  async getIntelligenceBrief(): Promise<IntelligenceBriefResponse> {
    const { data } = await apiClient.get('/api/v1/osint/signals/intelligence-brief');
    return data;
  },
};
